/**
 * /profile — Personal profile page.
 *
 * Shows the user's display name (editable) + the unified DIAN verification
 * card. The DIAN card owns identification AND email — when DIAN is verified,
 * email is implicitly verified too because the OTP was sent to the DIAN-
 * registered email. We never duplicate that information.
 *
 * App preferences (locale, timezone), the read-only account info (phone, role)
 * and logout live in /settings.
 */
import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Loader2, Mail, ShieldCheck } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { RequireAuth } from "@/components/RequireAuth";
import { AppShell } from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DianVerificationCard } from "@/components/profile/DianVerificationCard";
import { useProfile, useUpdateProfile } from "@/lib/api/profile.queries";
import type { ProfileData } from "@/lib/api/profile";

export const Route = createFileRoute("/profile")({
  component: ProfilePage,
});

function ProfilePage() {
  return (
    <RequireAuth>
      <AppShell>
        <ProfileContent />
      </AppShell>
    </RequireAuth>
  );
}

function ProfileContent() {
  const { t } = useTranslation();
  const profileQuery = useProfile();

  if (profileQuery.isPending) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-5 w-5 animate-spin text-primary" />
      </div>
    );
  }

  const profile = profileQuery.data?.profile;

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">
          {t("profile.page.title")}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("profile.page.subtitle")}
        </p>
      </div>

      {/* Display name */}
      <DisplayNameSection displayName={profile?.displayName ?? ""} />

      {/* Email preset — pulled from the DIAN record after verification */}
      <EmailSection profile={profile ?? null} />

      {/* Unified DIAN verification — identity + email */}
      <Card>
        <CardHeader>
          <CardTitle>{t("profile.verification.section_title")}</CardTitle>
          <CardDescription>
            {t("profile.verification.section_subtitle")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <DianVerificationCard
            current={
              profile?.identification ?? {
                type: null,
                number: null,
                dv: null,
                verified: false,
                verifiedAt: null,
                verifiedVia: null,
                legalNameDian: null,
                emailDian: null,
              }
            }
          />
        </CardContent>
      </Card>
    </div>
  );
}

// ── Email preset section ──────────────────────────────────────────────────────
// Mirrors the email preset on pahcertificar's profile page — read-only because
// `user_profiles.email` is owned by the unified DIAN verification flow (the
// DIAN lookup returns it from the same default tenant NIT we use to look up
// the user's identification). To change it, the user re-runs verification.

function EmailSection({ profile }: { profile: ProfileData | null }) {
  const { t } = useTranslation();
  const email = profile?.email ?? profile?.identification.emailDian ?? "";
  const verified = profile?.emailVerified === true;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("profile.email.title")}</CardTitle>
        <CardDescription>{t("profile.email.subtitle")}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid gap-1.5">
          <Label htmlFor="p-email">{t("profile.email.field")}</Label>
          <div className="relative">
            <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              id="p-email"
              type="email"
              value={email}
              readOnly
              autoComplete="email"
              placeholder={t("profile.email.empty_placeholder")}
              className="pl-9"
            />
          </div>
        </div>
        {verified ? (
          <p className="flex items-center gap-1.5 text-xs text-emerald-700">
            <ShieldCheck className="h-3.5 w-3.5" />
            {t("profile.email.verified_badge")}
          </p>
        ) : (
          <p className="text-xs text-muted-foreground">
            {t("profile.email.unverified_hint")}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

// ── Display name section ──────────────────────────────────────────────────────

function DisplayNameSection({
  displayName: initialDisplayName,
}: {
  displayName: string;
}) {
  const { t } = useTranslation();
  const [displayName, setDisplayName] = useState(initialDisplayName);
  const updateProfile = useUpdateProfile();

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = displayName.trim();
    if (!trimmed) return;
    try {
      await updateProfile.mutateAsync({ displayName: trimmed });
      toast.success(t("profile.display_name.saved"));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : t("profile.err_generic"));
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("profile.display_name.title")}</CardTitle>
        <CardDescription>{t("profile.display_name.subtitle")}</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSave} className="space-y-4">
          <div className="grid gap-1.5">
            <Label htmlFor="p-displayName">
              {t("profile.display_name.field")}
            </Label>
            <Input
              id="p-displayName"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              autoComplete="name"
              placeholder={t("profile.display_name.placeholder")}
            />
          </div>
          <Button
            type="submit"
            size="sm"
            disabled={updateProfile.isPending || !displayName.trim()}
          >
            {updateProfile.isPending && (
              <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
            )}
            {t("profile.display_name.save_cta")}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
